import type { QueryKey } from '@tanstack/react-query'

import { StandardRPCJsonSerializer } from '@orpc/client/standard'

const serializer = new StandardRPCJsonSerializer()

/**
 * The default `queryKeyHashFn`, built on the recipe in oRPC's TanStack Query guide.
 * TanStack's own hasher throws on a bigint. oRPC's JSON serializer turns bigints, dates and
 * other non-JSON values in plain objects and arrays into JSON and records their types in
 * `meta`, so `1n` and `'1'` hash apart. It leaves class instances as they are, and an SDK
 * entity in a key carries bigint fields, so the final stringify tags any bigint it still
 * finds instead of relying on a global `BigInt.prototype.toJSON`. It also sorts the keys of
 * every object, class instances included, and the sorted `meta` makes the hash independent
 * of key order.
 *
 * A key that refers back to itself throws a `TypeError`, as `JSON.stringify` does, before
 * the serializer can recurse without end.
 */
export function hashQueryKey(queryKey: QueryKey): string {
  assertAcyclic(queryKey, [])
  const [json, meta] = serializer.serialize(queryKey)
  return JSON.stringify([json, meta.map(entry => JSON.stringify(entry)).sort()], stableReplacer)
}

function stableReplacer(this: Record<string, unknown>, key: string, value: unknown): unknown {
  // `value` has already been through `toJSON`; read the holder to see the raw bigint.
  const raw = this[key]
  if (typeof raw === 'bigint') return { $bigint: raw.toString() }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value
  const record = value as Record<string, unknown>
  return Object.fromEntries(
    Object.keys(record)
      .sort()
      .map(k => [k, record[k]])
  )
}

/**
 * Walks what the serializer and the stringify walk: arrays, maps, sets and own enumerable
 * fields. A class instance with `toJSON` is stringified through that method, so its fields
 * are skipped.
 */
function assertAcyclic(value: unknown, ancestors: object[]): void {
  if (value === null || typeof value !== 'object') return
  if (!isPlainObject(value) && typeof (value as { toJSON?: unknown }).toJSON === 'function') {
    return
  }
  if (ancestors.includes(value)) {
    throw new TypeError('Cannot hash a circular query key')
  }
  const children =
    value instanceof Map
      ? [...value.entries()]
      : value instanceof Set
        ? [...value]
        : Object.values(value)
  ancestors.push(value)
  for (const child of children) assertAcyclic(child, ancestors)
  ancestors.pop()
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}
